(function () {
  /* Perf (2026-08-05 PSI loop): ~21KB of class eval deferred to the first
     pointer/key/touch/scroll/click. The widget's HTML is fully server-rendered
     (prices, default tier, form inputs) so nothing is visually missing before
     boot; the capture-phase click listener guarantees even an assistive-tech
     click that skips pointerdown boots the widget BEFORE the event reaches it
     (listeners added mid-capture still receive the bubble phase). Money path:
     first tap = pointerdown boots + delegated listeners attach, then the
     click lands. Do not remove the click-capture belt. */
  var swBoot = function () {
  if (!customElements.get('subscription-widget')) {
    class SubscriptionWidget extends HTMLElement {
      connectedCallback() {
        this.queryDOM();

        // Abort controller for canceling pending variant fetches
        this.abortController = null;

        // Event delegation for clicks — survives innerHTML replacement
        this.addEventListener('click', this.handleClick);
        this.addEventListener('change', this.handleChange);

        // Listen for variant changes on the parent section
        this.sectionEl = this.closest('.shopify-section, dialog');
        if (this.sectionEl) {
          this.sectionEl.addEventListener('variant:update', this.handleVariantUpdate);
        }

        this.syncWithProductForm();
        this.syncDefaultTier();

        // Index-lock the inactive card to the active card's default tier on load.
        const initialTier = this.activeTierEl();
        if (initialTier) this.syncSiblingTier(initialTier);

        // Publish the initial selection so satellites can read it on connect
        // (before the first user interaction fires an event).
        this.currentSelection = this.buildSelection();

        // ...and ANNOUNCE it. This widget boots on first interaction (see the gate at the
        // bottom of this file), so every satellite — value bars, LTO bar, sticky ATC, member
        // benefits, hero swap — has already connected, found no `currentSelection`, and
        // rendered its empty state by the time we get here. Publishing silently left them
        // stale until the shopper happened to change a tier: the savings bars read
        // "Save £0 · 0% off" in red underneath a card advertising 52% off (found in a
        // session replay, 2026-08-06). Satellites treat this exactly like a tier change.
        this.dispatchEvent(new CustomEvent('subscription:tier-change', {
          bubbles: true,
          detail: this.currentSelection
        }));
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('change', this.handleChange);

        if (this.sectionEl) {
          this.sectionEl.removeEventListener('variant:update', this.handleVariantUpdate);
        }

        // Cancel any pending fetch
        if (this.abortController) {
          this.abortController.abort();
        }
      }

      queryDOM() {
        this.subscribeCard = this.querySelector('[data-card="subscribe"]');
        this.onetimeCard = this.querySelector('[data-card="onetime"]');
        this.tiers = this.querySelectorAll('.sw-tier');
        this.priceDisplay = this.querySelector('[data-price-display="subscribe"]');
        this.sellingPlanInput = this.querySelector('.sw-selling-plan-input');
      }

      // ---- Event delegation handlers ----

      handleClick = (e) => {
        // Tier button click
        const tier = e.target.closest('.sw-tier');
        if (tier) {
          e.stopPropagation();
          if (!tier.disabled) this.selectTier(tier); // sold-out tier
          return;
        }

        // Backdrop click closes the dialog. The dialog has no padding, so a click whose
        // target IS the dialog and lands outside its box can only be the ::backdrop.
        if (e.target instanceof HTMLDialogElement && e.target.classList.contains('sw-dialog')) {
          const r = e.target.getBoundingClientRect();
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) {
            e.target.close();
            return;
          }
        }

        // Details popup trigger (subscription)
        const detailsTrigger = e.target.closest('.sw-details-trigger:not(.sw-details-trigger--onetime)');
        if (detailsTrigger) {
          const dialog = this.querySelector('.sw-dialog:not(.sw-dialog--onetime)');
          if (dialog) dialog.showModal();
          return;
        }

        // Details popup trigger (one-time)
        const onetimeDetailsTrigger = e.target.closest('.sw-details-trigger--onetime');
        if (onetimeDetailsTrigger) {
          const dialog = this.querySelector('.sw-dialog--onetime');
          if (dialog) dialog.showModal();
          return;
        }

        // Dialog close button
        const closeBtn = e.target.closest('.sw-dialog__close');
        if (closeBtn) {
          const dialog = closeBtn.closest('.sw-dialog');
          if (dialog) dialog.close();
          return;
        }

        // Let links navigate normally
        if (e.target.closest('a')) return;

        // Subscribe card click
        if (e.target.closest('[data-card="subscribe"]')) {
          this.selectCard('subscribe');
          return;
        }

        // One-time card click
        if (e.target.closest('[data-card="onetime"]')) {
          this.selectCard('onetime');
        }
      };

      handleChange = (e) => {
        if (e.target.name && e.target.name.startsWith('purchase_type_')) {
          this.selectCard(e.target.value);
        }
      };

      // ---- Variant update (re-render from section HTML) ----

      handleVariantUpdate = (event) => {
        // CRITICAL: Skip if this event was triggered by our own tier selection
        // Otherwise we'd reset the user's selection with server defaults
        const sourceId = event.detail?.sourceId;
        if (sourceId === this.id || sourceId === 'subscription-widget') {
          return;
        }

        // Handle combined listings where variant change swaps the product
        if (event.detail.data.newProduct) {
          this.dataset.productId = event.detail.data.newProduct.id;
        } else if (
          event.target instanceof HTMLElement &&
          event.target.dataset.productId !== this.dataset.productId
        ) {
          return;
        }

        const html = event.detail.data.html;
        if (!html) return;

        const newWidget = html.querySelector('subscription-widget');
        if (!newWidget) return;

        // Carry over updated data attributes
        this.dataset.variantId = newWidget.dataset.variantId;
        this.dataset.basePrice = newWidget.dataset.basePrice;

        // Replace content and re-query DOM references
        this.innerHTML = newWidget.innerHTML;
        this.queryDOM();
        this.syncWithProductForm();
      };

      // ---- Card & tier selection ----

      // ---- Selection contract (single source of truth for satellite blocks) ----
      // The widget publishes the complete current selection as `this.currentSelection`
      // and in the subscription:tier-change event detail, including a `tiers` summary of
      // ALL visible tiers so readers (e.g. the Value Bar) can compute any scale without
      // reaching into the widget's DOM. Hidden tiers aren't rendered, so they're naturally
      // excluded.
      activeTierEl() {
        if (this.dataset.mode === 'onetime') {
          return this.querySelector('.sw-tiers--onetime .sw-tier--active');
        }
        return this.querySelector('.sw-card--subscribe .sw-tier--active');
      }

      visibleTierSummary() {
        return Array.from(this.querySelectorAll('.sw-card--subscribe .sw-tier')).map((t) => ({
          savingsPercent: parseFloat(t.dataset.savingsPercent) || 0,
          savingsAmount: Math.max(0, (parseInt(t.dataset.comparePrice, 10) || 0) - (parseInt(t.dataset.price, 10) || 0)),
          monthCount: parseInt(t.dataset.monthCount, 10) || 0
        }));
      }

      buildSelection() {
        const isOnetime = this.dataset.mode === 'onetime';
        const tier = this.activeTierEl();
        const price = tier ? (parseInt(tier.dataset.price, 10) || 0) : 0;
        const compare = (tier && !isOnetime) ? (parseInt(tier.dataset.comparePrice, 10) || 0) : 0;
        return {
          variantId: tier ? (tier.dataset.variantId || null) : null,
          sellingPlanId: (tier && !isOnetime) ? (tier.dataset.sellingPlanId || null) : null,
          price: price,
          comparePrice: compare || null,
          savingsAmount: Math.max(0, compare - price),
          savingsPercent: (tier && !isOnetime) ? (parseFloat(tier.dataset.savingsPercent) || 0) : 0,
          monthCount: (tier && !isOnetime) ? (parseInt(tier.dataset.monthCount, 10) || 0) : 0,
          isOnetime: isOnetime,
          tiers: this.visibleTierSummary()
        };
      }

      selectCard(type) {
        const isSubscribe = type === 'subscribe';

        this.subscribeCard?.classList.toggle('sw-card--active', isSubscribe);
        this.onetimeCard?.classList.toggle('sw-card--active', !isSubscribe);

        const radio = this.querySelector(`input[value="${type}"]`);
        if (radio) radio.checked = true;

        this.dataset.mode = type;

        if (isSubscribe) {
          // Get active subscribe tier
          const activeTier = this.querySelector('.sw-card--subscribe .sw-tier--active');
          if (activeTier) {
            this.updateVariant(activeTier.dataset.variantId);
            this.updateSellingPlan(activeTier.dataset.sellingPlanId || '');
          }
        } else {
          // Get active one-time tier
          const activeTier = this.querySelector('.sw-tiers--onetime .sw-tier--active');
          if (activeTier) {
            this.updateVariant(activeTier.dataset.variantId);
          }
          this.updateSellingPlan('');
        }

        // Keep the published selection fresh for satellites reading on this event.
        this.currentSelection = this.buildSelection();

        this.dispatchEvent(new CustomEvent('subscription:card-change', {
          bubbles: true,
          detail: { type }
        }));
      }

      selectTier(tier) {
        const isOnetimeTier = tier.classList.contains('sw-tier--onetime');
        const cardType = isOnetimeTier ? 'onetime' : 'subscribe';

        this.selectCard(cardType);

        // Only toggle active state within the same tier group (subscribe or onetime)
        const tierGroup = isOnetimeTier ? '.sw-tiers--onetime' : '.sw-card--subscribe .sw-tiers';
        const tiersInGroup = this.querySelectorAll(`${tierGroup} .sw-tier`);
        tiersInGroup.forEach(t => { t.classList.remove('sw-tier--active'); t.setAttribute('aria-pressed', 'false'); });
        tier.classList.add('sw-tier--active');
        tier.setAttribute('aria-pressed', 'true');

        const price = parseInt(tier.dataset.price, 10);
        const variantId = tier.dataset.variantId;

        // Announce the new selection (visible price changes are otherwise silent to SR users).
        const announce = this.querySelector('[data-sw-announce]');
        const tierName = tier.querySelector('.sw-tier__name');
        if (announce) announce.textContent = (tierName ? tierName.textContent.trim() + ': ' : '') + this.formatMoney(price);

        if (isOnetimeTier) {
          // One-time tier: update price display (no compare price)
          const onetimePriceDisplay = this.querySelector('[data-price-display="onetime"]');
          if (onetimePriceDisplay) {
            const priceEl = onetimePriceDisplay.querySelector('.sw-price--regular');
            if (priceEl) priceEl.textContent = this.formatMoney(price);
          }

          // Toggle one-time benefits visibility
          const tierIndex = tier.dataset.tierIndex;
          const benefitBlocks = this.querySelectorAll('.sw-plan-benefits--onetime .sw-plan-benefits__option');
          benefitBlocks.forEach(block => {
            block.hidden = block.dataset.planIndex !== tierIndex;
          });

          // Update variant, no selling plan
          this.updateVariant(variantId);
          this.updateSellingPlan('');

        } else {
          // Subscribe tier: update price display with compare price
          const comparePrice = parseInt(tier.dataset.comparePrice, 10);
          const subscribePriceDisplay = this.querySelector('[data-price-display="subscribe"]');
          if (subscribePriceDisplay) {
            const compareEl = subscribePriceDisplay.querySelector('.sw-price--compare');
            const saleEl = subscribePriceDisplay.querySelector('.sw-price--sale');
            if (compareEl) compareEl.textContent = this.formatMoney(comparePrice);
            if (saleEl) saleEl.textContent = this.formatMoney(price);
          }

          // Toggle subscribe benefits visibility
          const tierIndex = tier.dataset.tierIndex;
          const benefitBlocks = this.querySelectorAll('.sw-card--subscribe .sw-plan-benefits__option');
          benefitBlocks.forEach(block => {
            block.hidden = block.dataset.planIndex !== tierIndex;
          });

          // Update variant and selling plan
          this.updateVariant(variantId);
          this.updateSellingPlan(tier.dataset.sellingPlanId);
        }

        // Index-lock the sibling card so both always show the same quantity
        // (e.g. 2-Month subscription ⇄ 2-Pack one-time) — keeps the price comparison honest.
        this.syncSiblingTier(tier);

        // Publish the complete selection (single source of truth) for satellite blocks.
        // Backward compatible: still carries sellingPlanId / price / savingsPercent / isOnetime,
        // now also variantId, comparePrice, savingsAmount, monthCount and a `tiers` summary.
        this.currentSelection = this.buildSelection();
        this.dispatchEvent(new CustomEvent('subscription:tier-change', {
          bubbles: true,
          detail: this.currentSelection
        }));
      }

      // Keep the two cards index-locked by quantity: a 2-Month subscription mirrors to the
      // 2-Pack one-time (and vice versa) so the displayed prices always compare like-for-like.
      // Matches by month-count ⇄ pack-count (robust to ordering / hidden tiers). Updates ONLY
      // the sibling card's display (active tier + header price + benefits) — never the active
      // card or the cart variant/plan, so the money path is untouched.
      syncSiblingTier(sourceTier) {
        const isOnetimeSource = sourceTier.classList.contains('sw-tier--onetime');
        const count = isOnetimeSource
          ? parseInt(sourceTier.dataset.packCount, 10)
          : parseInt(sourceTier.dataset.monthCount, 10);
        if (!count) return;

        const groupSel = isOnetimeSource ? '.sw-card--subscribe .sw-tiers' : '.sw-tiers--onetime';
        const countAttr = isOnetimeSource ? 'monthCount' : 'packCount';
        const siblingTiers = Array.from(this.querySelectorAll(`${groupSel} .sw-tier`));
        const match = siblingTiers.find((t) => parseInt(t.dataset[countAttr], 10) === count);
        if (!match || match.disabled) return; // no like-for-like (hidden or sold out) → leave sibling as-is

        siblingTiers.forEach((t) => { t.classList.remove('sw-tier--active'); t.setAttribute('aria-pressed', 'false'); });
        match.classList.add('sw-tier--active');
        match.setAttribute('aria-pressed', 'true');

        // Mirror the sibling card's header price (display only)
        if (isOnetimeSource) {
          const disp = this.querySelector('[data-price-display="subscribe"]');
          if (disp) {
            const c = disp.querySelector('.sw-price--compare');
            const s = disp.querySelector('.sw-price--sale');
            if (c) c.textContent = this.formatMoney(parseInt(match.dataset.comparePrice, 10));
            if (s) s.textContent = this.formatMoney(parseInt(match.dataset.price, 10));
          }
        } else {
          const disp = this.querySelector('[data-price-display="onetime"]');
          if (disp) {
            const p = disp.querySelector('.sw-price--regular');
            if (p) p.textContent = this.formatMoney(parseInt(match.dataset.price, 10));
          }
        }

        // Mirror the sibling card's benefits to the matched tier index
        const idx = match.dataset.tierIndex;
        const benefitSel = isOnetimeSource
          ? '.sw-card--subscribe .sw-plan-benefits__option'
          : '.sw-plan-benefits--onetime .sw-plan-benefits__option';
        this.querySelectorAll(benefitSel).forEach((b) => { b.hidden = b.dataset.planIndex !== idx; });
      }

      // ---- Variant sync (update variant ID in product form + trigger image change) ----

      updateVariant(variantId) {
        if (!variantId) return;

        // Update widget's data attribute
        this.dataset.variantId = variantId;

        // Update the product form's variant input
        const form = this.getProductForm();
        if (!form) return;

        let variantInput = form.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variantId;
        }

        // Fetch section HTML with new variant to trigger media gallery update
        this.fetchAndDispatchVariantUpdate(variantId);
      }

      // Fetch section HTML and dispatch variant:update event for media gallery
      async fetchAndDispatchVariantUpdate(variantId) {
        // Cancel any pending fetch to prevent race conditions
        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();

        try {
          // Build URL with variant parameter
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variantId);

          const response = await fetch(url.toString(), { signal: this.abortController.signal });
          const text = await response.text();
          const html = new DOMParser().parseFromString(text, 'text/html');

          // Find the specific variant data from the response
          // The variant-picker script contains an array of ALL variants, so we need to find the right one
          let variantData = { id: parseInt(variantId, 10), available: true };

          const variantScript = html.querySelector('variant-picker script[type="application/json"]');
          if (variantScript?.textContent) {
            try {
              const allVariants = JSON.parse(variantScript.textContent);
              // If it's an array, find the matching variant
              if (Array.isArray(allVariants)) {
                const matchingVariant = allVariants.find(v => v.id == variantId);
                if (matchingVariant) {
                  variantData = matchingVariant;
                }
              } else if (allVariants && allVariants.id) {
                // If it's a single variant object, use it
                variantData = allVariants;
              }
            } catch (e) {
              // Keep the default variantData
            }
          }

          // Dispatch variant:update event that media gallery listens to
          const section = this.closest('.shopify-section');
          if (section) {
            section.dispatchEvent(new CustomEvent('variant:update', {
              bubbles: true,
              detail: {
                resource: variantData,
                sourceId: this.id || 'subscription-widget',
                data: {
                  html,
                  productId: this.dataset.productId
                }
              }
            }));
          }

          // CRITICAL: Re-assert our variant ID after event dispatch
          // Other components (product-form.js) may have overwritten it
          const form = this.getProductForm();
          if (form) {
            const variantInput = form.querySelector('input[name="id"]');
            if (variantInput && variantInput.value != variantId) {
              variantInput.value = variantId;
            }
          }
        } catch (error) {
          // Ignore abort errors (expected when user clicks rapidly)
          if (error.name !== 'AbortError') {
            console.warn('Failed to fetch variant update:', error);
          }
        }
      }

      // ---- Selling plan sync (scoped to current section) ----
      // Uses ONLY the widget's hidden input with form attribute - no duplicate inputs

      updateSellingPlan(planId) {
        if (!this.sellingPlanInput) return;

        // Always update the widget's input value
        this.sellingPlanInput.value = planId || '';

        // Connect/disconnect from form based on whether we have a valid plan
        if (planId) {
          this.sellingPlanInput.setAttribute('form', 'BuyButtons-ProductForm-' + this.dataset.sectionId);
        } else {
          this.sellingPlanInput.removeAttribute('form');
        }

        // Clean up any stale selling_plan inputs that may exist inside the form
        // (from previous implementations or edge cases)
        const form = this.getProductForm();
        if (form) {
          const staleInputs = form.querySelectorAll('input[name="selling_plan"]');
          staleInputs.forEach(input => input.remove());
        }
      }

      syncWithProductForm() {
        const form = this.getProductForm();
        if (!form) return;

        // Clean up any stale selling_plan inputs inside the form
        // The widget's input with form attribute handles submission
        const staleInputs = form.querySelectorAll('input[name="selling_plan"]');
        staleInputs.forEach(input => input.remove());

        // Ensure widget input is properly connected if it has a value
        if (this.sellingPlanInput) {
          if (this.sellingPlanInput.value) {
            this.sellingPlanInput.setAttribute('form', 'BuyButtons-ProductForm-' + this.dataset.sectionId);
          } else {
            this.sellingPlanInput.removeAttribute('form');
          }
        }
      }

      // Sync form variant input and product images with the default active tier on page load
      syncDefaultTier() {
        var mode = this.dataset.mode;
        var activeTier;
        if (mode === 'subscribe') {
          activeTier = this.querySelector('.sw-card--subscribe .sw-tier--active');
        } else {
          activeTier = this.querySelector('.sw-tiers--onetime .sw-tier--active');
        }
        if (activeTier && activeTier.dataset.variantId) {
          var variantId = activeTier.dataset.variantId;
          var form = this.getProductForm();
          // No product form (e.g. a page lander like pdp-v1, which adds via the Super Button):
          // nothing to sync and no gallery listening — same guard as updateVariant(). Without it
          // this re-downloaded and parsed the whole ~800KB page on the first interaction.
          if (!form) return;
          var variantInput = form.querySelector('input[name="id"]');
          if (variantInput) variantInput.value = variantId;
          // Fetch correct variant images if not the first variant
          if (variantId != this.dataset.baseVariantId) {
            this.fetchAndDispatchVariantUpdate(variantId);
          }
        }
      }

      getProductForm() {
        const sectionId = this.dataset.sectionId;
        const section = this.closest('.shopify-section');

        // Prefer the form within our own section to avoid cross-contamination
        return document.getElementById(`BuyButtons-ProductForm-${sectionId}`)
          || (section && section.querySelector('form[action*="/cart/add"]'));
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const amount = (cents / 100).toFixed(2);

        return format
          .replace('{{amount}}', amount)
          .replace('{{amount_no_decimals}}', Math.round(cents / 100).toString())
          .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
          .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100).toString());
      }
    }

    customElements.define('subscription-widget', SubscriptionWidget);
  }
  };

  if (window.Shopify && window.Shopify.designMode) {
    swBoot();
  } else {
    var swEvs = ['pointerdown', 'keydown', 'touchstart', 'wheel'];
    var swFired = false;
    var swGo = function () {
      if (swFired) return;
      swFired = true;
      swEvs.forEach(function (e) { window.removeEventListener(e, swGo); });
      window.removeEventListener('click', swGo, true);
      swBoot();
    };
    swEvs.forEach(function (e) { window.addEventListener(e, swGo, { passive: true }); });
    window.addEventListener('click', swGo, true);
  }
})();
